import { MAX_CHAT_IMAGE_SIZE_MB, MAX_CHAT_PDF_SIZE_MB } from '@/app/ai/constants/chatAttachments';
import { messageManager } from '@/app/ai/session/MessageManager';
import { events } from '@/app/events/events';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { arrayBufferToBase64 } from '@/app/helpers/files';
import { focusAIAnalyst } from '@/app/helpers/focusGrid';
import { openAIAnalystAndRun } from '@/app/ui/helpers/openAIAnalystAndRun';
import { apiClient } from '@/shared/api/apiClient';
import { isSupportedImageMimeType, isSupportedPdfMimeType } from 'quadratic-shared/ai/helpers/files.helper';
import { createTextContent } from 'quadratic-shared/ai/helpers/message.helper';
import type { FileContent } from 'quadratic-shared/typesAndSchemasAI';
import type { Document } from 'quadratic-shared/typesAndSchemasDocuments';

const BYTES_PER_MB = 1024 * 1024;

/**
 * Reads a drive document into a chat attachment, or returns undefined when the
 * chat can't take it inline (a type the models don't accept as a file, or bytes
 * past the per-message ceiling). Carries `documentUuid` so the resulting pill
 * links back to the drive copy and no duplicate upload is made.
 */
const getChatAttachment = async (document: Document): Promise<FileContent | undefined> => {
  const mimeType = document.mimeType;
  const isImage = isSupportedImageMimeType(mimeType);
  if (!isImage && !isSupportedPdfMimeType(mimeType)) return undefined;

  const limitMb = isImage ? MAX_CHAT_IMAGE_SIZE_MB : MAX_CHAT_PDF_SIZE_MB;
  if (document.sizeBytes > limitMb * BYTES_PER_MB) return undefined;

  const response = await fetch(document.downloadUrl);
  if (!response.ok) {
    throw new Error(`Failed to download document ${document.uuid}: ${response.status}`);
  }

  return {
    type: 'data',
    data: arrayBufferToBase64(await response.arrayBuffer()),
    mimeType: isImage ? mimeType : 'application/pdf',
    fileName: document.name,
    documentUuid: document.uuid,
  };
};

/**
 * Chat about a drive document: opens the AI chat and attaches the document to
 * the draft message. PDFs and images go in as inline attachments; anything else
 * (DOCX, CSV, oversized files) seeds the prompt with the document's name so the
 * model reaches it through the `read_document` tool instead.
 *
 * With `intent: 'import'`, a request to import the file's data onto the sheet
 * is submitted immediately instead of drafting a message.
 */
export const chatWithDocument = (documentUuid: string, options?: { intent?: 'import' }): void => {
  void (async () => {
    let attachment: FileContent | undefined;
    let name: string;
    try {
      const { document } = await apiClient.documents.get(documentUuid);
      name = document.name;
      attachment = await getChatAttachment(document);
    } catch (error) {
      console.warn('[chatWithDocument] Failed to load document for the chat', error);
      gridSettings.snackbar('Could not add this file to the chat. Try again.', { severity: 'error' });
      return;
    }

    if (options?.intent === 'import') {
      // Attachable files (PDFs, images) go into the message itself; others
      // (DOCX, oversized) are named so the model reads them via `read_document`.
      const content = attachment
        ? [attachment, createTextContent('Import this file into the spreadsheet')]
        : [createTextContent(`Import the file “${name}” from my Drive into the spreadsheet`)];
      openAIAnalystAndRun(() => {
        events.emit('aiAnalystSubmitPrompt', {
          content,
          messageSource: 'DriveImport',
          context: {},
          // Appended to the ongoing chat; a lower index would truncate it.
          messageIndex: messageManager.getCurrentChat().messages.length,
          importFiles: [],
        });
        focusAIAnalyst();
      });
      return;
    }

    openAIAnalystAndRun(() => {
      if (attachment) {
        events.emit('aiAnalystAttachFiles', [attachment]);
      } else {
        events.emit('aiAnalystInsertText', `Using the file “${name}” from my Drive,`);
      }
      focusAIAnalyst();
    });
  })();
};
